import React, {
  MutableRefObject,
  useEffect,
  useState,
  ButtonHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';

import { useAddToBasket } from 'hooks/ProductPageV2/useAddToBasket';
import { getUri } from 'services/generic';
import { Cart } from 'types/Cart';
import {
  ProductType,
  SelectedProductAttributes,
} from 'types/ProductPageV2/Products';

import styles from './ProductAddBasket.module.scss';

type Props = {
  product?: ProductType;
  qty: number;
  onSizeRequired?: boolean;
  elementRef?: MutableRefObject<HTMLButtonElement | null>;
  openModal: (idModal: string) => void;
  closeModal: () => void;
  gtmMandatoryAttributes: SelectedProductAttributes[];
  classes?: Partial<ProductAddBasketClasses>;
  sendGtmEvent?: (cart: Cart) => void;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export default function ProductAddBasket({
  product,
  qty,
  onSizeRequired,
  elementRef,
  openModal,
  closeModal,
  gtmMandatoryAttributes,
  classes,
  sendGtmEvent,
  className,
  ...props
}: Props): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { editLine, addToBasket, updateBasket } = useAddToBasket();
  const [clicked, setClicked] = useState<boolean>(false);

  function getLabel() {
    if (editLine) return 'LABEL_UPDATE_MY_BASKET';

    return clicked ? 'LABEL_ADDED_TO_MY_BASKET' : 'LABEL_ADD_TO_MY_BASKET';
  }

  function handleClick() {
    if (clicked) {
      return;
    }

    if (!product) {
      onSizeRequired && openModal('productSize');
    } else {
      setClicked(true);
      editLine
        ? updateBasket(product, qty, editLine).then(() => {
            navigate(getUri('/checkout/cart'));
          })
        : addToBasket(
            product,
            qty,
            gtmMandatoryAttributes,
            openModal,
            closeModal
          ).then(updatedCart => {
            updatedCart && sendGtmEvent?.(updatedCart);
          });
    }
  }

  useEffect(() => {
    clicked && setClicked(false);
  }, [product]);

  return (
    <button
      className={classnames(
        styles.addButton,
        { [styles.clicked]: clicked },
        className,
        classes?.addToCartButton
      )}
      onClick={handleClick}
      ref={elementRef}
      {...props}
    >
      <span className={styles.label}>{t(getLabel())}</span>
    </button>
  );
}

export interface ProductAddBasketClasses {
  addToCartButton?: string;
}
